import React, { useState, useMemo } from 'react';

import { useTranslation } from 'react-i18next';
import { useMediaQuery } from 'react-responsive';
import { useQuery } from '@tanstack/react-query';
import AdminService from '../../../../../../services/AdminService';

import SearchInput from '../../../../../Form/SearchInput';
import Dropdown from '../../../../../Form/Dropdown';
import Timeline from '../../../../Jobs/MyJobs/JobDetails/Insights/Timeline';
import Card from '../../../../../common/Card';
import ColumnLayout from '../../../../../common/Layout/ColumnLayout';

import classes from './styles.module.scss';
import {
  getDate,
  getActivityDate,
  getTimelineTitle,
  getActivityTitle,
} from '../../../../../../helpers/getTimeline';

export default function Activities({ company }) {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState('');
  const [activeJobFilterOption, setActiveJobFilterOption] = useState('');

  const isTabletOrMobile = useMediaQuery({ query: '(max-width: 1024px)' });

  const { data: jobPages } = useQuery({
    queryKey: ['companyJobs', company],
    queryFn: () => {
      const filters = ['status:noteq:Draft'];
      filters.push(`company.id:eq:${company?.id}`);

      return AdminService.getCompanyJobs({
        pageNumber: 1,
        pageSize: 9999,
        Filters: filters,
      });
    },
    keepPreviousData: true,
  });

  const jobs = jobPages?.data;

  const jobFilterOptions = [
    {
      label: t('common.All', 'All'),
      value: '',
      onClick: () => setActiveJobFilterOption(''),
    },
    ...(jobs?.map((job) => ({
      label: job.jobTitle,
      value: job.id,
    })) ?? []),
  ];

  const { data: applicationTimeline } = useQuery({
    queryKey: [`applicationTimeline${company?.id}`],
    queryFn: () =>
      AdminService.getJobApplicationTimelineCompany({ id: company?.id }),
  });

  const { data: jobTimeline } = useQuery({
    queryKey: [`jobTimeline${company?.id}`],
    queryFn: () => AdminService.getJobTimelineCompany({ id: company?.id }),
  });

  const events = useMemo(() => {
    const applicationItems = (applicationTimeline ?? []).map((event) => {
      const eventDate = new Date(event?.createdAt);

      return {
        title: getTimelineTitle(event),
        date: getDate(eventDate),
        day: eventDate?.getDate(),
        month: eventDate?.getMonth(),
        year: eventDate?.getFullYear(),
        event,
      };
    });

    const jobItems = (jobTimeline ?? []).map((event) => {
      const eventDate = new Date(event?.createdAt);
      return {
        title: getTimelineTitle(event),
        date: getDate(eventDate),
        day: eventDate?.getDate?.(),
        month: eventDate?.getMonth?.(),
        year: eventDate?.getFullYear?.(),
        event,
      };
    });

    return [...applicationItems, ...jobItems];
  }, [applicationTimeline, jobTimeline]);

  const allActivities = useMemo(() => {
    const applicationItems = (applicationTimeline ?? []).map((activity) => {
      const eventDate = new Date(activity?.createdAt);

      return {
        id: activity?.id,
        text: getActivityTitle(activity),
        when: getActivityDate(eventDate),
        jobId: activity?.jobApplication?.jobPostId,
        activity,
      };
    });

    return [...applicationItems];
  }, [applicationTimeline]);

  const visibleEvents = useMemo(() => {
    const items = events ?? [];
    return items
      .slice()
      .sort(
        (prev, next) =>
          new Date(next?.event?.createdAt) - new Date(prev?.event?.createdAt)
      );
  }, [events]);

  const visibleActivities = useMemo(() => {
    const items = allActivities ?? [];

    // Case/diacritic-insensitive match helper
    const normalizeInvariant = (value) => {
      const v = (value ?? '').toString().toLowerCase();
      return v.normalize
        ? v.normalize('NFD').replace(/[\u0300-\u036f]/g, '')
        : v;
    };

    const term = normalizeInvariant(searchTerm?.trim?.());

    const filtered = items.filter((item) => {
      // Filter by jobId if a job filter is active
      const matchesJob =
        !activeJobFilterOption ||
        `${item?.jobId}` === `${activeJobFilterOption.value}`;

      // Filter by text if search term provided (invariant contains)
      const matchesText =
        !term || normalizeInvariant(item?.text).includes(term);

      return matchesJob && matchesText;
    });

    return filtered
      .slice()
      .sort(
        (prev, next) =>
          new Date(next?.activity?.createdAt) -
          new Date(prev?.activity?.createdAt)
      );
  }, [allActivities, searchTerm, activeJobFilterOption]);

  return (
    <div className={classes.Activities}>
      <ColumnLayout className={classes.Layout}>
        <ColumnLayout.Column className={classes.ListColumn}>
          <Card>
            <Card.Header>
              <div className={classes.SectionHeader}>
                <p className={classes.SectionTitle}>
                  {t('company.activities.title', 'Activities')}
                </p>
                <div className={classes.HeaderControls}>
                  <SearchInput
                    height={45}
                    value={searchTerm}
                    style={{
                      borderColor: '#F2F4F8',
                      backgroundColor: '#FFF',
                      boxShadow: '0 -4px 60px 0 rgba(62, 63, 99, 0.10)',
                    }}
                    setValue={setSearchTerm}
                    placeholder={t('common.search', 'Search')}
                  />
                  <Dropdown
                    width={isTabletOrMobile ? 139 : 180}
                    height={45}
                    activeOption={activeJobFilterOption}
                    setActiveOption={setActiveJobFilterOption}
                    options={jobFilterOptions}
                    placeholder={t('common.selectJob', 'Select Job')}
                    style={{
                      borderRadius: '16px',
                      border: '1px solid #F2F4F8',
                      background: '#FFF',
                      boxShadow: '0 -4px 60px 0 rgba(62, 63, 99, 0.10)',
                    }}
                  />
                </div>
              </div>
            </Card.Header>
            <Card.Body scrollable lassName={classes.ActivitiesBody}>
              {visibleActivities.length > 0 ? (
                <div className={classes.List}>
                  {visibleActivities.map((item) => (
                    <div key={item.id} className={classes.ListItem}>
                      <div className={classes.ItemText}>{item.text}</div>
                      <div className={classes.ItemWhen}>{item.when}</div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className={classes.EmptyText}>
                  {t('company.activities.noActivities', 'No activities found')}
                </div>
              )}
            </Card.Body>
          </Card>
        </ColumnLayout.Column>

        <ColumnLayout.Column className={classes.TimelineColumn}>
          <Card>
            <Card.Header>
              <div className={classes.SectionHeader}>
                <h3 className={classes.SectionTitle}>
                  {t('company.activities.timeline', 'Timeline')}
                </h3>
              </div>
            </Card.Header>
            <Card.Body scrollable lassName={classes.TimelineBody}>
              {visibleEvents.length > 0 ? (
                <Timeline data={visibleEvents} />
              ) : (
                <div className={classes.EmptyText}>
                  {t('company.activities.noTimeline', 'No timeline found')}
                </div>
              )}
            </Card.Body>
          </Card>
        </ColumnLayout.Column>
      </ColumnLayout>
    </div>
  );
}
